// src/lib/propertyEpisodes.js — the episode grouper for the property history.
// ----------------------------------------------------------------------------
// PURE. No React, no fetch, no i18n — takes the /events payload, returns
// episodes. Kept pure so `node --test` can drive it against a committed slice of
// real production data (propertyEpisodes.fixture.json), which is the only way
// the six rules in core/PROPERTY_HISTORY_TIMELINE.md are actually verifiable.
//
// WHY EPISODES: the raw log is a database talking. 400 real events on one flat
// are ~25 human actions — an owner re-posting, an agent copying to three
// portals, four ads dying, one automated currency fix. The grouper's whole job
// is to recover the action from its exhaust.
//
// HARD INVARIANT — THE DRAWER IS LOSSLESS. Every input event lands in exactly
// one episode's `events` array. Grouping is compression for cognition, never
// deletion: an admin forensic view that silently drops rows is the failure this
// feature exists to prevent. `weight: 'hidden'` means "do not draw attention",
// NOT "discard" (the losslessness test pins this).
//
// The renderer decides how an episode LOOKS. This file decides only what
// happened. No strings here — labels live in the component with the ka/en pair.

const WINDOW_MS = 120_000   // one "sitting": an agent's copy-to-3-portals burst
const BIRTH_MS = 300_000    // an upsert this close to the ad's listed_at IS its posting

// Verbs that are pure machine exhaust — the crawler pinging, facts landing,
// bookkeeping stamps. Never an episode of their own; always someone's drawer.
const EXHAUST = new Set([
  'claim.committed', 'property.feature', 'listing.meta_stamped',
  'property.inventory_added', 'listing.viewed', 'property.updated',
  'property.context_signal', 'property.created',
])

const RESHARE = new Set(['listing.reshare_declared', 'listing.reshare_registered'])

// A price diff is REAL only when both sides are known. 3,748 of 4,345 price
// events in production have a null side — a partial scrape that failed to read
// the price, emitting "price removed" then "price restored". Rendering those as
// movement is the single most misleading thing this UI could do.
export function realPriceMove(ev) {
  if (ev?.verb !== 'listing.changed') return null
  const p = ev.data?.price
  if (!p || typeof p !== 'object') return null
  if (p.from == null || p.to == null) return null
  const from = Number(p.from), to = Number(p.to)
  if (!Number.isFinite(from) || !Number.isFinite(to) || from === to) return null
  return { from, to, currency: p.currency || null, direction: to > from ? 'up' : 'down' }
}

// no_op is decided SERVER-side (core_events.js) because the before/after it
// needs is gated money — a client-side test would silently disagree with itself
// depending on who is looking. Absent flag = unknowable, which is not "false":
// we degrade to a low-weight episode rather than assert it was noise.
export const isNoOpAmend = ev => ev?.no_op === true
export const isUnverifiableAmend = ev =>
  /^(cooperation|listing_terms)\.[a-z_]*amended$/.test(ev?.verb || '') && ev?.no_op === undefined

// Lineage depth by walking parent_listing_id. Measured across production:
// depth 1 = 98.3% of reposts, depth 2 = 1.4%, max observed 4. Cap the walk so a
// cyclic edge can never hang the render.
export function lineageDepth(listingId, listings, cap = 8) {
  let d = 0, cur = listingId, seen = new Set()
  while (cur && d < cap) {
    const parent = listings?.[cur]?.parent_listing_id
    if (!parent || seen.has(parent)) break
    seen.add(parent); cur = parent; d++
  }
  return d
}

function classify(ev, listings, firstUpsert) {
  const v = ev.verb || ''
  if (v === 'property.match_judged') return 'merge'
  if (v === 'listing.currency_corrected') return 'correction'
  if (RESHARE.has(v)) return 'reshare'
  if (v === 'listing.state') {
    const val = ev.data?.value
    if (val === 'status_withdrawn') return 'withdrawn'
    if (val === 'status_sold') return 'sold'
    return 'exhaust'                       // status_active on first sight adds nothing
  }
  if (v === 'listing.changed') return realPriceMove(ev) ? 'price_move' : 'exhaust'
  if (v === 'listing.upserted') {
    const l = listings?.[ev.subject_id]
    if (!l || l.author_kind !== 'owner' || l.parent_listing_id) return 'exhaust'
    // "The owner posted" is the upsert that coincides with the ad's OWN
    // listed_at — not the first one we happen to hold. Two wrong answers were
    // tried first (2026-07-27, both caught by the fixture): every upsert →
    // "posted" 7 times for 4 posts; first-in-page → posted on 24 Jul when the
    // real posting was 14 Jul, because the true first sighting is on an older
    // page. listed_at is the ad's own fact and does not move with pagination.
    const born = Date.parse(l.listed_at || '')
    if (!Number.isFinite(born)) return firstUpsert?.get(ev.subject_id) === ev.id ? 'owner_post' : 'exhaust'
    return Math.abs(tOf(ev) - born) <= BIRTH_MS ? 'owner_post' : 'exhaust'
  }
  if (v.startsWith('cooperation.') || v.startsWith('listing_terms.')) {
    if (isNoOpAmend(ev)) return 'bookkeeping'
    return 'agreement'
  }
  if (EXHAUST.has(v)) return 'exhaust'
  return 'unknown'                          // ghost row — never hidden, never dropped
}

const actorKey = ev => ev.actor_party_id || `kind:${ev.actor_kind || 'system'}`
const tOf = ev => Date.parse(ev.occurred_at || '') || 0

// Category → how loud. The renderer maps these to type/colour; it does not
// re-decide them, so "why is this shouting" has one answer in one place.
const WEIGHT = {
  agreement: 'loud', price_move: 'loud', owner_post: 'loud', correction: 'loud', sold: 'loud',
  reshare: 'normal', unknown: 'normal', merge: 'normal',
  withdrawn: 'quiet',
  exhaust: 'hidden', bookkeeping: 'hidden',
}

/**
 * @param {{events:Array, listings:Object}} payload  straight from getPropertyEvents
 * @returns {Array} episodes, newest first
 */
export function buildEpisodes(payload) {
  const listings = payload?.listings || {}
  const events = [...(payload?.events || [])].sort((a, b) => tOf(a) - tOf(b) || (a.id - b.id))

  const episodes = []
  const openByKey = new Map()          // grouping key → episode still accepting events
  const mergeSeen = new Map()          // other_property → episode (dedup, §4.6)

  // First upsert per listing, over the events we actually hold. NOTE: with
  // has_more, page 2's older events can contain an EARLIER first sighting — so
  // this is "first known", and an owner_post episode can be re-classified when
  // older pages load. That is honest for a stream that grows backwards.
  const firstUpsert = new Map()
  for (const ev of events) {
    if (ev.verb === 'listing.upserted' && !firstUpsert.has(ev.subject_id)) firstUpsert.set(ev.subject_id, ev.id)
  }

  const push = (ep) => { episodes.push(ep); return ep }

  for (const ev of events) {
    const cat = classify(ev, listings, firstUpsert)
    const lid = ev.subject_type === 'listing' ? ev.subject_id : null
    const listing = lid ? listings[lid] : null

    // Merges dedupe by WHAT was linked, not by adjacency: the nightly matcher
    // re-emits the same verdict (3 identical in one night is normal).
    if (cat === 'merge') {
      const other = ev.data?.other_property || null
      const hit = other && mergeSeen.get(other)
      if (hit) { hit.events.push(ev); continue }
      const ep = push({
        key: `merge:${other || ev.id}`, kind: 'merge', at: ev.occurred_at,
        actor: { kind: 'system', name: null, party_id: null },
        other_property: other, verdict: ev.data?.verdict || null,
        listings: [], sources: [], price: null, depth: 0,
        weight: WEIGHT.merge, events: [ev],
      })
      if (other) mergeSeen.set(other, ep)
      continue
    }

    // Everything else groups by (category, actor, subject-ish, sitting window).
    // Reshares deliberately drop the listing from the key so one agent's fan-out
    // to three portals is ONE episode ("copied to 3 portals"), which is how a
    // human would describe it.
    const subjectPart = cat === 'reshare' ? 'fan' : (lid || ev.subject_id || '-')
    const gkey = `${cat}|${actorKey(ev)}|${subjectPart}`
    const open = openByKey.get(gkey)
    if (open && tOf(ev) - open._last <= WINDOW_MS) {
      open.events.push(ev)
      open._last = tOf(ev)
      if (lid && !open.listings.includes(lid)) open.listings.push(lid)
      if (listing?.source && !open.sources.includes(listing.source)) open.sources.push(listing.source)
      if (cat === 'price_move' && !open.price_move) open.price_move = realPriceMove(ev)
      continue
    }

    const author = listing ? { kind: listing.author_kind, name: listing.author_name, party_id: listing.author_party_id } : null
    // For a reshare the ACTOR is whoever pressed the button (event actor), which
    // is not always the resulting listing's stored author.
    const actor = cat === 'reshare' || !author
      ? { kind: ev.actor_kind === 'system' ? 'system' : (author?.kind || 'our_agent'), name: author?.name || null, party_id: ev.actor_party_id || null }
      : author

    const ep = push({
      key: `${gkey}|${ev.id}`, kind: cat, at: ev.occurred_at,
      actor,
      listings: lid ? [lid] : [],
      sources: listing?.source ? [listing.source] : [],
      price: listing && listing.price != null
        ? { value: Number(listing.price), currency: listing.currency || null, usd: listing.price_usd != null ? Number(listing.price_usd) : null }
        : null,
      price_move: cat === 'price_move' ? realPriceMove(ev) : null,
      depth: lid ? lineageDepth(lid, listings) : 0,
      unverifiable: isUnverifiableAmend(ev) || undefined,
      weight: isUnverifiableAmend(ev) ? 'quiet' : WEIGHT[cat] || 'normal',
      events: [ev],
    })
    ep._last = tOf(ev)
    openByKey.set(gkey, ep)
  }

  for (const ep of episodes) delete ep._last
  return episodes.reverse()               // newest first, as the stream arrives
}

// Convenience for the header: is a live copy materially below the agreed floor?
// Same-currency (USD-normalised) and percentage-based, because a 4 USD gap is FX
// rounding, not undercutting — and it is compared to the AGREEMENT FLOOR, never
// to the owner's own ad (agents legitimately price above the owner; that is the
// commission, not a breach).
export function belowFloor(priceUsd, floorUsd, pct = 3) {
  if (priceUsd == null || floorUsd == null) return null
  const f = Number(floorUsd), p = Number(priceUsd)
  if (!Number.isFinite(f) || !Number.isFinite(p) || f <= 0) return null
  const delta = ((f - p) / f) * 100
  return delta > pct ? { under_pct: Math.round(delta * 10) / 10, floor_usd: f, price_usd: p } : null
}

export const VISIBLE_WEIGHTS = ['loud', 'normal', 'quiet']
export const isVisible = ep => VISIBLE_WEIGHTS.includes(ep.weight)
